import { useEffect, useMemo, useState } from "react";
import { api, API, getAuthToken } from "@/lib/api";
import { downloadServerFile } from "@/lib/download";
import { formatMoney } from "@/lib/format";
import { useSettings } from "@/lib/settings";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Plus, Receipt, Download, ImageIcon, Trash2, FileSpreadsheet, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { LargeTitle, IOS_ACCENT } from "@/components/mobile/ios";
import PullToRefresh from "@/components/mobile/PullToRefresh";

const FALLBACK_CATEGORIES = ["Other"];

export default function Expenses() {
  const { user, permissions } = useAuth();
  const { settings } = useSettings();
  const categories = settings?.expense_categories?.length ? settings.expense_categories : FALLBACK_CATEGORIES;
  const currency = settings?.currency || { code: "INR", symbol: "₹" };
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const canCreate = isAdmin || permissions?.can_track_finance;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/expenses");
      setItems(r.data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const total = useMemo(() => items.reduce((s, e) => s + Number(e.amount || 0), 0), [items]);
  const byCat = useMemo(() => {
    const m = {};
    items.forEach(e => { m[e.category] = (m[e.category] || 0) + Number(e.amount || 0); });
    return m;
  }, [items]);

  const downloadReport = (fmt) => {
    try {
      downloadServerFile(`/reports/expenses`, { fmt });
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <Layout
      title="Financials"
      subtitle="Expense ledger"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadReport("pdf")} data-testid="expenses-download-pdf">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
          <Button variant="outline" onClick={() => downloadReport("excel")} data-testid="expenses-download-excel">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          {canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="h-10 px-4 rounded-xl btn-warm text-[13px] font-semibold" data-testid="expense-add-button">
                  <Plus className="h-4 w-4 mr-1.5" /> Log expense
                </Button>
              </DialogTrigger>
              <CreateExpenseDialog
                categories={categories}
                defaultCurrency={currency}
                onCreated={(e) => { setItems(prev => [e, ...prev]); setOpen(false); }}
              />
            </Dialog>
          )}
        </div>
      }
    >
      {/* ============ MOBILE iOS view ============ */}
      <div className="lg:hidden">
        <PullToRefresh onRefresh={load}>
          <LargeTitle title="Financials" eyebrow="Expense ledger" />

          {/* KPI grid */}
          <div className="px-4 grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl p-4 border border-black/[0.04]" style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
              <div className="text-[12px] text-[#8E8E93] font-medium uppercase tracking-wider">Total spend</div>
              <div className="text-[24px] font-bold tracking-tight mt-1 leading-tight">{formatMoney(total, currency)}</div>
              <div className="text-[12px] text-[#8E8E93] mt-1">{items.length} records</div>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-black/[0.04]" style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
              <div className="text-[12px] text-[#8E8E93] font-medium uppercase tracking-wider">Top category</div>
              <div className="text-[16px] font-bold tracking-tight mt-1 leading-tight truncate">
                {Object.entries(byCat).sort((a,b)=>b[1]-a[1])[0]?.[0] || "—"}
              </div>
              <div className="text-[12px] text-[#8E8E93] mt-1">{Object.keys(byCat).length} categories</div>
            </div>
          </div>

          {/* Download actions */}
          <div className="mx-4 mt-4 grid grid-cols-2 gap-2">
            <button
              onClick={() => downloadReport("pdf")}
              data-testid="expenses-download-pdf-mobile"
              className="bg-white rounded-xl py-3 flex items-center justify-center gap-1.5 border border-black/[0.04] text-[14px] font-medium text-[#1C1C1E] active:bg-black/[0.04]"
              style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
            >
              <FileText size={16} /> PDF
            </button>
            <button
              onClick={() => downloadReport("excel")}
              data-testid="expenses-download-excel-mobile"
              className="bg-white rounded-xl py-3 flex items-center justify-center gap-1.5 border border-black/[0.04] text-[14px] font-medium text-[#1C1C1E] active:bg-black/[0.04]"
              style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
            >
              <FileSpreadsheet size={16} /> Excel
            </button>
          </div>

          {/* Expenses list */}
          {items.length === 0 ? (
            <div className="mx-4 mt-6 bg-white rounded-2xl py-12 text-center">
              <div className="text-[15px] text-[#8E8E93]">No expenses yet.</div>
              <div className="text-[13px] text-[#8E8E93] mt-1">Tap + to log one.</div>
            </div>
          ) : (
            <div className="mx-4 mt-6 bg-white rounded-2xl overflow-hidden border border-black/[0.04]" style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
              {items.map((e, i) => {
                const last = i === items.length - 1;
                return (
                  <button
                    key={e.expense_id}
                    type="button"
                    onClick={() => e.receipt_path && setViewing(e)}
                    data-testid={`mobile-expense-${e.expense_id}`}
                    className={`w-full text-left flex items-center gap-3 px-4 py-3.5 active:bg-black/[0.04] transition ${!last ? "border-b border-black/[0.05]" : ""}`}
                  >
                    <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#FFF4E6", color: IOS_ACCENT }}>
                      <Receipt size={18} strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-semibold leading-tight text-[#1C1C1E] truncate">{e.vendor}</div>
                      <div className="text-[13px] text-[#8E8E93] mt-0.5 truncate">
                        {e.category} · {e.payment_date}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[15px] font-bold text-[#1C1C1E]">
                        {formatMoney(e.amount, { code: e.currency, symbol: currencySymbolFor(e.currency, currency) })}
                      </div>
                      {e.receipt_path && (
                        <div className="text-[11px] text-[#E07A1F] font-medium mt-0.5 flex items-center justify-end gap-0.5">
                          <ImageIcon size={11} /> receipt
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* FAB - mobile */}
          {canCreate && (
            <button
              onClick={() => setOpen(true)}
              className="fixed right-5 bottom-24 h-14 w-14 rounded-full grid place-items-center text-white z-30"
              style={{ background: IOS_ACCENT, boxShadow: "0 10px 22px -6px rgba(224,122,31,0.5), 0 2px 6px rgba(0,0,0,0.1)" }}
              data-testid="expense-fab-create-mobile"
              aria-label="Log expense"
            >
              <Plus size={26} strokeWidth={2.5} />
            </button>
          )}

          <div className="h-32" />
        </PullToRefresh>
      </div>

      {/* ============ DESKTOP view (iOS-style) ============ */}
      <div className="hidden lg:block">
      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="soft-card p-6" data-testid="kpi-total">
          <div className="eyebrow">Total spend</div>
          <div className="text-[32px] font-bold tracking-tight mt-2 text-[#1C1C1E]">{formatMoney(total, currency)}</div>
          <div className="text-[12px] text-[#8E8E93] mt-1">this period</div>
        </div>
        <div className="soft-card p-6">
          <div className="eyebrow">Records</div>
          <div className="text-[32px] font-bold tracking-tight mt-2 text-[#1C1C1E]">{items.length}</div>
          <div className="text-[12px] text-[#8E8E93] mt-1">logged</div>
        </div>
        <div className="soft-card p-6">
          <div className="eyebrow">Categories</div>
          <div className="text-[32px] font-bold tracking-tight mt-2 text-[#1C1C1E]">{Object.keys(byCat).length}</div>
          <div className="text-[12px] text-[#8E8E93] mt-1">in use</div>
        </div>
        <div className="soft-card p-6">
          <div className="eyebrow">Top category</div>
          <div className="text-[18px] font-bold tracking-tight mt-2 leading-tight text-[#1C1C1E] truncate">
            {Object.entries(byCat).sort((a,b)=>b[1]-a[1])[0]?.[0] || "—"}
          </div>
          <div className="text-[12px] font-semibold mt-1 text-[#E07A1F]">
            {formatMoney(Object.entries(byCat).sort((a,b)=>b[1]-a[1])[0]?.[1] || 0, currency)}
          </div>
        </div>
      </div>

      <div className="soft-card overflow-hidden">
        <div className="px-6 py-4 border-b border-black/[0.05] flex items-center justify-between">
          <div>
            <div className="eyebrow">Records</div>
            <h3 className="text-[18px] font-bold tracking-tight mt-1 text-[#1C1C1E]">All expenses</h3>
          </div>
          <span className="text-[12px] text-[#8E8E93]">{items.length} records</span>
        </div>

        {/* Table header */}
        <div className="grid grid-cols-[140px_1fr_180px_140px_120px_80px] gap-4 px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8E8E93] border-b border-black/[0.05]">
          <div>Date</div>
          <div>Vendor</div>
          <div>Category</div>
          <div>Submitted by</div>
          <div className="text-right">Amount</div>
          <div className="text-right">Receipt</div>
        </div>

        {items.length === 0 && (
          <div className="px-6 py-12 text-center text-[#8E8E93] text-[14px]">No expenses logged yet.</div>
        )}
        {items.map((e, i) => (
          <div
            key={e.expense_id}
            data-testid={`expense-row-${e.expense_id}`}
            className={`grid grid-cols-[140px_1fr_180px_140px_120px_80px] gap-4 px-6 py-4 hover:bg-black/[0.02] items-center ${i !== items.length - 1 ? "border-b border-black/[0.04]" : ""}`}
          >
            <div className="text-[13px] font-mono text-[#52525B]">{e.payment_date}</div>
            <div className="text-[14px] font-semibold text-[#1C1C1E] truncate">{e.vendor}</div>
            <div>
              <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold" style={{ background: "#FFF4E6", color: "#A56014" }}>
                {e.category}
              </span>
            </div>
            <div className="text-[13px] text-[#52525B] truncate">{e.created_by_name}</div>
            <div className="text-right text-[14px] font-bold tracking-tight text-[#1C1C1E]">
              {formatMoney(e.amount, { code: e.currency, symbol: currencySymbolFor(e.currency, currency) })}
            </div>
            <div className="text-right">
              {e.receipt_path ? (
                <button
                  onClick={() => setViewing(e)}
                  data-testid={`expense-receipt-view-${e.expense_id}`}
                  className="inline-flex items-center justify-center h-7 w-7 rounded-lg"
                  style={{ background: "#FFF4E6", color: "#E07A1F" }}
                  title="View receipt"
                >
                  <Receipt size={13} strokeWidth={2.2} />
                </button>
              ) : <span className="text-[#C7C7CC] text-xs">—</span>}
            </div>
          </div>
        ))}
      </div>
      </div>{/* end desktop view */}

      {/* Receipt viewer (shared mobile + desktop) */}
      <Sheet open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <SheetContent className="w-full sm:max-w-[520px]" data-testid="receipt-sheet">
          <SheetTitle className="font-display text-xl">Receipt — {viewing?.vendor}</SheetTitle>
          {viewing && <ReceiptViewer expense={viewing} />}
        </SheetContent>
      </Sheet>

    </Layout>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="tech-card p-5">
      <div className="overline">{label}</div>
      <div className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight truncate">{value}</div>
    </div>
  );
}

// Map a stored currency code to its symbol (fall back to the active workspace currency)
const SYMBOL_BY_CODE = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", AUD: "A$", CAD: "C$",
  SGD: "S$", AED: "AED", ZAR: "R", NGN: "₦", KES: "KSh",
};
function currencySymbolFor(code, fallback) {
  if (!code) return fallback?.symbol || "₹";
  return SYMBOL_BY_CODE[code] || fallback?.symbol || code;
}

function Th({ children, className = "" }) {
  return <th className={`py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-neutral-600 text-left ${className}`}>{children}</th>;
}

function CreateExpenseDialog({ onCreated, categories, defaultCurrency }) {
  const [form, setForm] = useState({
    amount: "",
    currency: defaultCurrency?.code || "INR",
    category: categories?.[0] || "Other",
    vendor: "",
    payment_date: new Date().toISOString().slice(0, 10),
    description: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.amount || !form.vendor || !form.payment_date) {
      toast.error("Amount, vendor, and date are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("receipt", file);
      const r = await api.post("/expenses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Expense logged");
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="sm:max-w-[520px]" data-testid="expense-create-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Log expense</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount">
            <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" />
          </Field>
          <Field label="Currency">
            <Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} data-testid="expense-currency-input" />
          </Field>
        </div>
        <Field label="Vendor / Paid to">
          <Input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} data-testid="expense-vendor-input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select className="h-10 border border-neutral-200 rounded-md px-3 text-sm w-full bg-white"
              value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              data-testid="expense-category-select">
              {(categories || ["Other"]).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Payment date">
            <Input type="date" value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} data-testid="expense-date-input" />
          </Field>
        </div>
        <Field label="Description (optional)">
          <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="expense-description-input" />
        </Field>
        <Field label="Receipt image (optional)">
          <Input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="expense-receipt-input" />
        </Field>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={submitting} className="bg-neutral-950 hover:bg-neutral-800 text-white" data-testid="expense-submit-button">
          {submitting ? "Saving…" : "Save expense"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="overline text-[10px]">{label}</Label>
      {children}
    </div>
  );
}

function ReceiptViewer({ expense }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let active = true;
    let blobUrl;
    (async () => {
      try {
        const r = await api.get(`/expenses/${expense.expense_id}/receipt`, { responseType: "blob" });
        blobUrl = URL.createObjectURL(r.data);
        if (active) setSrc(blobUrl);
      } catch (e) {}
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [expense.expense_id]);

  return (
    <div className="mt-4">
      <div className="text-sm text-neutral-700 mb-2">
        <span className="font-medium">
          {formatMoney(expense.amount, { code: expense.currency, symbol: SYMBOL_BY_CODE[expense.currency] || "" })}
        </span> · {expense.category} · {expense.payment_date}
      </div>
      {src ? (
        <img src={src} alt="receipt" className="w-full max-h-[70vh] object-contain border border-neutral-200 rounded-md" data-testid="receipt-image" />
      ) : (
        <div className="h-64 grid place-items-center border border-dashed border-neutral-300 rounded-md text-neutral-400 text-sm">
          Loading receipt…
        </div>
      )}
    </div>
  );
}
